import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AgentSummary,
  AgentStats,
  LogEntry,
  LogsFilter,
  emailAgentService,
} from '../../services/emailAgentService';
import { AgentConfigModal } from './AgentConfigModal';

const AGENT_LABELS: Record<string, string> = {
  ddq: 'DDQ Email Agent',
  feedback: 'Feedback Agent',
  ingest: 'Ingest Agent',
};

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-green-100 text-green-800',
  stopped: 'bg-gray-100 text-gray-600',
  error: 'bg-red-100 text-red-800',
  success: 'text-green-700',
  failed: 'text-red-700',
  in_progress: 'text-yellow-700',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function extractStartStopErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;

  const maybeErr = err as {
    response?: {
      data?: {
        detail?: unknown;
        missing_fields?: unknown;
      };
    };
    message?: unknown;
  };

  const detail = maybeErr.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  if (detail && typeof detail === 'object') {
    const nested = detail as { message?: unknown; missing_fields?: unknown };
    if (Array.isArray(nested.missing_fields) && nested.missing_fields.length > 0) {
      return `Configuration incomplete. Missing: ${nested.missing_fields.join(', ')}`;
    }
    if (typeof nested.message === 'string' && nested.message.trim()) {
      return nested.message.trim();
    }
  }

  const missingFields = maybeErr.response?.data?.missing_fields;
  if (Array.isArray(missingFields) && missingFields.length > 0) {
    return `Configuration incomplete. Missing: ${missingFields.join(', ')}`;
  }

  if (typeof maybeErr.message === 'string' && maybeErr.message.trim()) {
    return maybeErr.message.trim();
  }

  return fallback;
}

export default function EmailAgentsDashboard() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [filters, setFilters] = useState<LogsFilter>({ page: 1, page_size: 15 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pendingActions, setPendingActions] = useState<Record<string, 'starting' | 'stopping'>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [configModalAgent, setConfigModalAgent] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearPendingAction = useCallback((agentType: string) => {
    const timeout = pendingTimeoutsRef.current[agentType];
    if (timeout) {
      clearTimeout(timeout);
      delete pendingTimeoutsRef.current[agentType];
    }
    setPendingActions((prev) => {
      if (!prev[agentType]) return prev;
      const next = { ...prev };
      delete next[agentType];
      return next;
    });
  }, []);

  const setPendingAction = useCallback((agentType: string, action: 'starting' | 'stopping') => {
    const existingTimeout = pendingTimeoutsRef.current[agentType];
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    setPendingActions((prev) => ({ ...prev, [agentType]: action }));
    pendingTimeoutsRef.current[agentType] = setTimeout(() => {
      clearPendingAction(agentType);
    }, 30_000);
  }, [clearPendingAction]);

  const fetchAll = useCallback(async () => {
    try {
      const [agentList, agentStats, logsResp] = await Promise.all([
        emailAgentService.listAgents(),
        emailAgentService.getStats(),
        emailAgentService.getLogs(filters),
      ]);
      setAgents(agentList);
      setStats(agentStats);
      setLogs(logsResp.items);
      setLogTotal(logsResp.total);

      // Keep the button in "Processing..." until backend reports target status.
      setPendingActions((prev) => {
        const next = { ...prev };
        for (const agent of agentList) {
          const pending = next[agent.agent_type];
          if (!pending) continue;
          const reachedTarget =
            (pending === 'starting' && agent.status === 'running') ||
            (pending === 'stopping' && agent.status !== 'running');
          if (reachedTarget) {
            const timeout = pendingTimeoutsRef.current[agent.agent_type];
            if (timeout) {
              clearTimeout(timeout);
              delete pendingTimeoutsRef.current[agent.agent_type];
            }
            delete next[agent.agent_type];
          }
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch email agent data', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchAll, 10_000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchAll]);

  useEffect(() => {
    return () => {
      Object.values(pendingTimeoutsRef.current).forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current = {};
    };
  }, []);

  const handleStartStop = async (agentType: string, running: boolean) => {
    const action = running ? 'stopping' : 'starting';
    setActionErrors((prev) => {
      if (!prev[agentType]) return prev;
      const next = { ...prev };
      delete next[agentType];
      return next;
    });
    setPendingAction(agentType, action);
    try {
      if (running) {
        await emailAgentService.stopAgent(agentType);
      } else {
        await emailAgentService.startAgent(agentType);
      }
      await fetchAll();
    } catch (err) {
      console.error('Start/stop failed', err);
      clearPendingAction(agentType);
      const fallback = running ? 'Failed to stop agent.' : 'Failed to start agent.';
      const message = extractStartStopErrorMessage(err, fallback);
      setActionErrors((prev) => ({ ...prev, [agentType]: message }));
    }
  };

  /** Stop then start after config save — runs in background; card shows Processing… like Start/Stop. */
  const restartAgentInBackground = useCallback(
    (agentType: string) => {
      void (async () => {
        try {
          setPendingAction(agentType, 'stopping');
          await emailAgentService.stopAgent(agentType);
          await fetchAll();
          setPendingAction(agentType, 'starting');
          await emailAgentService.startAgent(agentType);
          await fetchAll();
        } catch (err) {
          console.error('Background restart failed', err);
          clearPendingAction(agentType);
        }
      })();
    },
    [setPendingAction, clearPendingAction, fetchAll]
  );

  const totalPages = Math.max(1, Math.ceil(logTotal / (filters.page_size || 15)));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Email Agents Dashboard</h1>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded"
            checked={autoRefresh}
            onChange={() => setAutoRefresh((p) => !p)}
          />
          Auto-refresh (10 s)
        </label>
      </div>

      {/* Stats summary cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Processed Today" value={stats.total_today} />
          <StatCard
            label="Success Rate"
            value={`${(stats.success_rate * 100).toFixed(0)}%`}
          />
          <StatCard
            label="Avg Processing Time"
            value={stats.avg_processing_time_ms ? `${Math.round(stats.avg_processing_time_ms)} ms` : '—'}
          />
        </div>
      )}

      {/* Agent cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {agents.map((agent) => {
          const isPending = Boolean(pendingActions[agent.agent_type]);
          const isRunning = agent.status === 'running';
          const buttonClass = isPending
            ? 'bg-amber-50 text-amber-700 cursor-wait'
            : isRunning
              ? 'bg-red-50 text-red-700 hover:bg-red-100'
              : 'bg-green-50 text-green-700 hover:bg-green-100';

          return (
            <div
              key={agent.agent_type}
              className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
            >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">
                {AGENT_LABELS[agent.agent_type] || agent.agent_type}
              </h3>
              <StatusBadge status={agent.status} />
            </div>

            <div className="text-sm text-gray-500 space-y-1 mb-4">
              <p>
                Processed today:{' '}
                <span className="font-medium text-gray-700">{agent.processed_today}</span>
              </p>
              <p>
                Success rate:{' '}
                <span className="font-medium text-gray-700">
                  {(agent.success_rate * 100).toFixed(0)}%
                </span>
              </p>
              {agent.last_heartbeat && (
                <p>
                  Last heartbeat:{' '}
                  <span className="font-medium text-gray-700">
                    {new Date(agent.last_heartbeat).toLocaleTimeString()}
                  </span>
                </p>
              )}
              {agent.is_configured === false && (
                <p className="text-xs text-amber-700">
                  Not configured{agent.missing_fields?.length ? `: ${agent.missing_fields.join(', ')}` : ''}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleStartStop(agent.agent_type, agent.status === 'running')}
                disabled={isPending}
                className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-80 ${buttonClass}`}
              >
                {isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Processing...
                  </span>
                ) : (
                  isRunning ? 'Stop' : 'Start'
                )}
              </button>
              <button
                onClick={() => setConfigModalAgent(agent.agent_type)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Configure
              </button>
            </div>
            {actionErrors[agent.agent_type] && (
              <p className="mt-2 text-xs text-red-600">{actionErrors[agent.agent_type]}</p>
            )}
            </div>
          );
        })}
      </div>

      {/* Daily chart (simple bar) */}
      {stats && stats.daily_counts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Emails Processed (Last 7 Days)</h3>
          <div className="flex items-end gap-2 h-32">
            {[...stats.daily_counts].reverse().map((d) => {
              const maxCount = Math.max(...stats.daily_counts.map((x) => x.count), 1);
              const pct = (d.count / maxCount) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-500">{d.count}</span>
                  <div
                    className="w-full bg-blue-500 rounded-t"
                    style={{ height: `${Math.max(pct, 4)}%` }}
                  />
                  <span className="text-[10px] text-gray-400">
                    {new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity log filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Activity Log</h3>
          <div className="flex gap-2">
            <select
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              value={filters.agent_type || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, agent_type: e.target.value || undefined, page: 1 }))
              }
            >
              <option value="">All Agents</option>
              <option value="ddq">DDQ</option>
              <option value="feedback">Feedback</option>
              <option value="ingest">Ingest</option>
            </select>
            <select
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              value={filters.status || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value || undefined, page: 1 }))
              }
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>
        </div>

        {/* Log table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2 pr-3">Timestamp</th>
                <th className="pb-2 pr-3">Agent</th>
                <th className="pb-2 pr-3">Event</th>
                <th className="pb-2 pr-3">Sender</th>
                <th className="pb-2 pr-3">Subject</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                  >
                    <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 font-medium text-gray-700">
                      {AGENT_LABELS[log.agent_type] || log.agent_type}
                    </td>
                    <td className="py-2 pr-3">{log.event_type}</td>
                    <td className="py-2 pr-3 text-gray-500 truncate max-w-[150px]">
                      {log.sender_email || '—'}
                    </td>
                    <td className="py-2 pr-3 text-gray-500 truncate max-w-[200px]">
                      {log.subject || '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusBadge status={log.status} />
                    </td>
                    <td className="py-2 text-gray-500">
                      {log.processing_time_ms != null ? `${log.processing_time_ms} ms` : '—'}
                    </td>
                  </tr>
                  {expandedLogId === log.id && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 p-4">
                        {log.error_message && (
                          <p className="text-red-600 text-xs mb-2">
                            <strong>Error:</strong> {log.error_message}
                          </p>
                        )}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                        {!log.error_message &&
                          (!log.details || Object.keys(log.details).length === 0) && (
                            <p className="text-xs text-gray-400">No additional details.</p>
                          )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No activity logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <span className="text-sm text-gray-500">
              Page {filters.page} of {totalPages} ({logTotal} total)
            </span>
            <div className="flex gap-2">
              <button
                disabled={(filters.page || 1) <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) - 1 }))}
                className="px-3 py-1 rounded border text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={(filters.page || 1) >= totalPages}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}
                className="px-3 py-1 rounded border text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Config Modal */}
      {configModalAgent && (
        <AgentConfigModal
          agentType={configModalAgent}
          onClose={() => setConfigModalAgent(null)}
          onSaved={() => {
            setConfigModalAgent(null);
            fetchAll();
          }}
          onRestartAgentInBackground={restartAgentInBackground}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
    </div>
  );
}
